import type { Account, Connection } from "@shared/types/connection"
import { useMemo, useRef, useState } from "react"

import { useAccounts } from "../hooks/useAccounts"
import { useLclConnection } from "../hooks/useLclConnection"

type Step = "credentials" | "connecting" | "done"

function initialStep(conn: Connection | undefined): Step {
  if (!conn) return "credentials"
  return "connecting"
}

// 10-slot digit input.
//
// One real <input type="tel"> overlaid (opacity-0, inset-0, z-index 1) captures
// all keyboard input and is the only element the user actually taps — so iOS/Android
// reliably shows the keyboard and reopens it after it's dismissed.
// The 10 visual slots below are readOnly inputs (pointer-events-none) which give
// us the exact same styling as the original design without any <div> height issues.
// autoComplete="off" prevents the password-manager popup.
function ClientIdInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const LEN = 10
  const inputRef = useRef<HTMLInputElement>(null)
  const cursor = Math.min(value.length, LEN - 1)
  // Track selection range so we can highlight selected slots visually
  const [focused, setFocused] = useState(false)

  return (
    <div
      className="relative cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {/* The real input — invisible overlay that captures all keyboard events.
          Stays focused the whole time; opacity-0 hides it without hiding the
          visual slots underneath. autoComplete="off" kills the password-manager. */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        autoComplete="off"
        autoFocus
        value={value}
        maxLength={LEN}
        onChange={(e) => {
          const next = e.target.value.replace(/\D/g, "").slice(0, LEN)
          onChange(next)
          if (next.length >= LEN) inputRef.current?.blur()
        }}
        onFocus={(e) => {
          setFocused(true)
          // After blur+re-focus the cursor lands at 0 — move to end so
          // backspace always deletes the last digit
          const len = e.target.value.length
          e.target.setSelectionRange(len, len)
        }}
        onBlur={() => setFocused(false)}
        onSelect={(e) => {
          // Collapse any selection back to end — selection is handled visually
          // by the slots and the native highlight on an invisible input is confusing
          const len = e.currentTarget.value.length
          e.currentTarget.setSelectionRange(len, len)
        }}
        className="absolute inset-0 z-[1] h-full w-full cursor-text caret-transparent opacity-0 focus:outline-none"
      />
      {/* Visual slots — readOnly inputs (same styling as the original design).
          pointer-events-none so all taps fall through to the overlay above. */}
      <div className="pointer-events-none flex justify-center gap-1">
        {Array.from({ length: LEN }, (_, i) => {
          const char = value[i] ?? ""
          const isActive = i === cursor && value.length < LEN
          return (
            <input
              key={i}
              type="text"
              readOnly
              tabIndex={-1}
              value={char}
              className={`w-6 min-w-0 rounded-none border-0 border-b-2 pb-0.5 text-center font-mono text-lg text-gray-900 caret-transparent transition-colors focus:outline-none dark:text-white ${focused ? "bg-gray-100 dark:bg-black/30" : "bg-transparent"} ${
                isActive
                  ? focused
                    ? "border-green-500"
                    : "border-gray-300 dark:border-gray-600"
                  : char
                    ? "border-gray-500 dark:border-gray-400"
                    : "border-gray-300 dark:border-gray-600"
              }`}
            />
          )
        })}
      </div>
    </div>
  )
}

function PinPad({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const digits = useMemo(() => {
    const d = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]
    for (let i = d.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[d[i], d[j]] = [d[j], d[i]]
    }
    return d
  }, [])

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Progress dots — always rendered to avoid layout shift */}
      <div className="flex items-center gap-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={`h-3 w-3 rounded-full transition-colors ${
              i < value.length
                ? "bg-green-600 dark:bg-green-500"
                : "bg-gray-200 dark:bg-gray-600"
            }`}
          />
        ))}
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault()
            onChange("")
          }}
          disabled={value.length === 0}
          className="ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-500 hover:bg-gray-200 disabled:cursor-default disabled:opacity-30 dark:bg-gray-800 dark:hover:bg-gray-700"
        >
          ×
        </button>
      </div>

      {/* Numpad — two explicit flex rows so buttons keep exact h-12 w-12 → perfect circles.
          onPointerDown + preventDefault instead of onClick prevents ghost clicks:
          when the modal opens via a tap, the browser fires a synthetic click event
          300ms later at the same coordinates. Using pointerdown (which fires immediately
          on touch) and cancelling it stops that ghost click from reaching our handler. */}
      <div className="flex flex-col items-center gap-2">
        {[digits.slice(0, 5), digits.slice(5)].map((row, ri) => (
          <div key={ri} className="flex gap-2">
            {row.map((d) => (
              <button
                key={d}
                type="button"
                style={{
                  borderRadius: "100%",
                  border: "none",
                }}
                onPointerDown={(e) => {
                  e.preventDefault()
                  if (value.length < 6) onChange(value + d)
                }}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-medium text-gray-700 outline-none focus:outline-none focus:ring-0 active:opacity-75 dark:active:text-green-500 [@media(hover:hover)]:hover:opacity-75 dark:[@media(hover:hover)]:hover:text-green-500"
              >
                {d}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
      {message}
    </div>
  )
}

export function LclModal({
  onClose,
  connections,
  existingLclConnection,
}: {
  onClose: () => void
  connections: Connection[]
  existingLclConnection?: Connection
}) {
  const [step, setStep] = useState<Step>(initialStep(existingLclConnection))
  const [pendingConnectionId, setPendingConnectionId] = useState<string | null>(
    existingLclConnection?.oid ?? null,
  )

  const [clientId, setClientId] = useState("")
  const [password, setPassword] = useState("")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const {
    connect,
    confirm,
    loading: connectLoading,
    error: connectError,
  } = useLclConnection()

  const { accounts } = useAccounts(
    step === "connecting" ? pendingConnectionId : null,
  )

  const activeAccounts = accounts.filter((a) => a.status === "active")
  const discoveredAccounts = accounts.filter((a) => a.status === "discovered")

  const pendingConnection = connections.find(
    (c) => c.oid === pendingConnectionId,
  )

  const hasAccounts = activeAccounts.length > 0 || discoveredAccounts.length > 0
  const isLoggingIn = !!pendingConnection?.loginStep

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const connectionId = await connect(clientId, password)
    if (connectionId) {
      setPendingConnectionId(connectionId)
      setClientId("")
      setPassword("")
      setStep("connecting")
    }
  }

  // "Try again" from error state: reset to credentials.
  // connectLcl will lazily clean up the stale/error connection on next submit.
  const handleBack = () => {
    setPendingConnectionId(null)
    setStep("credentials")
  }

  const handleConfirm = async () => {
    if (!pendingConnectionId) return
    const activeIds = activeAccounts.map((a) => a.oid)
    const allSelected = [...activeIds, ...Array.from(selected)]
    setConfirmError(null)
    setConfirming(true)
    try {
      await confirm(pendingConnectionId, allSelected)
      setStep("done")
    } catch (err) {
      setConfirmError((err as Error).message)
    } finally {
      setConfirming(false)
    }
  }

  const toggleAccount = (account: Account) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(account.oid)) {
        next.delete(account.oid)
      } else {
        next.add(account.oid)
      }
      return next
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-black/50 [@media(pointer:fine)]:items-center [@media(pointer:fine)]:justify-center">
      <div className="flex h-full w-full flex-col overflow-y-auto overflow-x-hidden bg-white shadow-xl dark:bg-gray-900 [@media(pointer:fine)]:h-[608px] [@media(pointer:fine)]:max-h-[calc(100vh-2rem)] [@media(pointer:fine)]:w-[360px] [@media(pointer:fine)]:max-w-[calc(100vw-2rem)] [@media(pointer:fine)]:rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 pt-6">
          <h2 className="text-base font-semibold">LCL Connect</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-transparent text-xl text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            ×
          </button>
        </div>

        {/* px-4 on mobile keeps the PinPad (5×48px + 4×8px gap = 272px) from
            overflowing on small phones (320px − 32px padding = 288px > 272px ✓).
            px-8 on desktop (pointer:fine) restores comfortable margins. */}
        <div className="flex flex-1 flex-col px-4 pb-8 pt-2 [@media(pointer:fine)]:px-8">
          {/* Step: credentials */}
          {step === "credentials" && (
            <form
              onSubmit={(e) => void handleCredentialsSubmit(e)}
              className="flex flex-col gap-6"
            >
              {/* Client ID */}
              <div className="flex flex-col gap-3">
                <h3 className="text-center text-sm font-bold text-gray-800 dark:text-gray-100">
                  Your client ID
                </h3>
                <ClientIdInput value={clientId} onChange={setClientId} />
              </div>

              {/* PIN */}
              <div className="flex flex-col gap-3">
                <h3 className="text-center text-sm font-bold text-gray-800 dark:text-gray-100">
                  Your PIN
                </h3>
                <PinPad value={password} onChange={setPassword} />
              </div>

              <button
                type="submit"
                disabled={
                  connectLoading || clientId.length < 10 || password.length < 6
                }
                className="w-full rounded-full bg-green-600 py-2.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {connectLoading ? "Connecting…" : "Connect →"}
              </button>
              {connectError && <ErrorBox message={connectError.message} />}
            </form>
          )}

          {/* Step: connecting — unified login progress + account list */}
          {step === "connecting" && (
            <div className="flex flex-1 flex-col">
              {pendingConnection?.status === "error" ? (
                <>
                  <ErrorBox
                    message={
                      pendingConnection.errorMessage ?? "Authentication error"
                    }
                  />
                  <button
                    onClick={handleBack}
                    className="w-full rounded-lg bg-gray-100 py-2.5 text-sm font-medium dark:bg-gray-800"
                  >
                    Try again
                  </button>
                </>
              ) : (
                <>
                  {/* Top content — grows to fill available space */}
                  <div className="flex-1">
                    {/* Spinner + status */}
                    {(isLoggingIn || !hasAccounts) && (
                      <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {isLoggingIn
                            ? "Logging in…"
                            : "Waiting for account discovery…"}
                        </p>
                        {isLoggingIn &&
                          pendingConnection?.loginStep?.message && (
                            <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                              {pendingConnection.loginStep.message}
                            </span>
                          )}
                      </div>
                    )}

                    {/* Account list */}
                    {hasAccounts && (
                      <div className="flex flex-col">
                        {activeAccounts.map((account) => (
                          <div
                            key={account.oid}
                            className="flex items-start gap-3 border-t border-gray-100 py-2.5 dark:border-gray-700"
                          >
                            <span className="flex-1 text-sm">
                              {account.displayName}
                            </span>
                            <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
                              active
                            </span>
                          </div>
                        ))}
                        {discoveredAccounts.length > 0 && (
                          <div className="mt-3">
                            <p className="mb-1 px-3 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                              New accounts found
                            </p>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                              {discoveredAccounts.map((account) => (
                                <label
                                  key={account.oid}
                                  className="flex cursor-pointer items-center gap-3 py-2.5 first:rounded-t-lg last:rounded-b-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected.has(account.oid)}
                                    onChange={() => toggleAccount(account)}
                                    className="h-4 w-4 accent-green-600"
                                  />
                                  <span className="flex-1 text-sm">
                                    {account.displayName}
                                  </span>
                                  {account.lastBalance !== 0 && (
                                    <span className="font-mono text-xs text-gray-400">
                                      {account.lastBalance.toFixed(2)}
                                    </span>
                                  )}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bottom — always pinned */}
                  <div className="mt-auto flex flex-col gap-3 pt-6">
                    {confirmError && <ErrorBox message={confirmError} />}
                    {discoveredAccounts.length > 0 && (
                      <button
                        onClick={() => void handleConfirm()}
                        disabled={confirming || selected.size === 0}
                        className="w-full rounded-lg bg-green-600 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                      >
                        {confirming ? "Confirming…" : "Confirm"}
                      </button>
                    )}
                    {discoveredAccounts.length === 0 &&
                      activeAccounts.length > 0 && (
                        <button
                          onClick={onClose}
                          className="w-full rounded-lg bg-green-600 py-2.5 text-sm font-medium text-white"
                        >
                          Done
                        </button>
                      )}
                    {/* TODO: trigger lcl_discover.py via a Python server instead of asking the user to run it manually */}
                    <button
                      onClick={() =>
                        alert(
                          "Run `python experimental/scripts/lcl_discover.py` from the repo root to discover your accounts.",
                        )
                      }
                      className="text-center text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      How to load accounts?
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step: done */}
          {step === "done" && (
            <div className="flex flex-1 flex-col">
              <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl font-bold text-green-600 dark:bg-green-900/40 dark:text-green-400">
                  ✓
                </div>
                <div>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">
                    Connected!
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Your accounts are ready.
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-full rounded-full bg-green-600 py-2.5 text-sm font-medium text-white"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
